/** Feature flag — set to true to enable pixel art drawing on MI / Structure tiles. */
export const PIXEL_ART_ENABLED = true;

/** Feature flag — set to false to disable legacy image-URL upload on MI tiles. */
export const IMAGE_UPLOAD_ENABLED = false;

export const PIXEL_GRID_SIZE = 25;

/** 16 curated colors for the pixel-art palette. Index 0 = transparent/eraser. */
export const PIXEL_PALETTE: readonly string[] = [
  'transparent', // 0  eraser
  '#000000', // 1  black
  '#ffffff', // 2  white
  '#ff0000', // 3  red
  '#00cc00', // 4  green
  '#0066ff', // 5  blue
  '#ffcc00', // 6  yellow
  '#ff6600', // 7  orange
  '#cc00ff', // 8  purple
  '#00cccc', // 9  cyan
  '#ff66cc', // 10 pink
  '#8b4513', // 11 brown
  '#888888', // 12 grey
  '#44aa44', // 13 dark green
  '#224488', // 14 navy
  '#cc3333', // 15 dark red
] as const;

/**
 * Compact pixel-art data for a single 25×25 image.
 * Stored as a flat Uint8Array of length 625 (25*25).
 * Each byte is a palette index (0–15). 0 = transparent.
 */
export class PixelArtData {
  readonly pixels: Uint8Array;

  constructor(source?: Uint8Array | number[]) {
    if (source) {
      this.pixels = source instanceof Uint8Array ? source : new Uint8Array(source);
      if (this.pixels.length !== PIXEL_GRID_SIZE * PIXEL_GRID_SIZE) {
        throw new Error(
          `PixelArtData expects ${PIXEL_GRID_SIZE * PIXEL_GRID_SIZE} pixels, got ${this.pixels.length}`,
        );
      }
    } else {
      this.pixels = new Uint8Array(PIXEL_GRID_SIZE * PIXEL_GRID_SIZE);
    }
  }

  get(x: number, y: number): number {
    return this.pixels[y * PIXEL_GRID_SIZE + x];
  }

  set(x: number, y: number, paletteIndex: number): void {
    this.pixels[y * PIXEL_GRID_SIZE + x] = paletteIndex;
  }

  /** Check if this art is completely empty (all transparent). */
  get isEmpty(): boolean {
    return this.pixels.every((v) => v === 0);
  }

  clone(): PixelArtData {
    return new PixelArtData(new Uint8Array(this.pixels));
  }

  equals(other: PixelArtData): boolean {
    for (let i = 0; i < this.pixels.length; i++) {
      if (this.pixels[i] !== other.pixels[i]) return false;
    }
    return true;
  }

  /** Flood fill starting at (x, y) with the given palette index. */
  floodFill(x: number, y: number, paletteIndex: number): void {
    const target = this.get(x, y);
    if (target === paletteIndex) return;
    const size = PIXEL_GRID_SIZE;
    const stack: [number, number][] = [[x, y]];
    const visited = new Uint8Array(size * size);
    while (stack.length > 0) {
      const [cx, cy] = stack.pop()!;
      const idx = cy * size + cx;
      if (cx < 0 || cx >= size || cy < 0 || cy >= size) continue;
      if (visited[idx]) continue;
      if (this.pixels[idx] !== target) continue;
      visited[idx] = 1;
      this.pixels[idx] = paletteIndex;
      stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
    }
  }

  /** Draw a filled square brush centered at (cx, cy). brushSize is 1–7. */
  drawBrush(cx: number, cy: number, brushSize: number, paletteIndex: number): void {
    const half = Math.floor(brushSize / 2);
    const size = PIXEL_GRID_SIZE;
    for (let dy = -half; dy <= half; dy++) {
      for (let dx = -half; dx <= half; dx++) {
        const px = cx + dx;
        const py = cy + dy;
        if (px >= 0 && px < size && py >= 0 && py < size) {
          this.pixels[py * size + px] = paletteIndex;
        }
      }
    }
  }

  /** Draw a brush stroke along a line from (x0,y0) to (x1,y1) using Bresenham. */
  drawLine(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    brushSize: number,
    paletteIndex: number,
  ): void {
    let dx = Math.abs(x1 - x0);
    let dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    let cx = x0;
    let cy = y0;
    for (;;) {
      this.drawBrush(cx, cy, brushSize, paletteIndex);
      if (cx === x1 && cy === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        cx += sx;
      }
      if (e2 <= dx) {
        err += dx;
        cy += sy;
      }
    }
  }

  // --- Serialization ---

  /**
   * RLE-compress palette indices → nibble-pack → base64.
   * Format: "R" prefix + base64 of RLE-then-nibble-packed data.
   * RLE encoding: each run is (count-1, value) as nibble pairs.
   * Max run length per entry = 16 (nibble 0–15 encodes counts 1–16).
   */
  toBase64(): string {
    const rle = rleEncode(this.pixels);
    const packedLen = Math.ceil(rle.length / 2);
    const packed = new Uint8Array(packedLen);
    for (let i = 0; i < rle.length; i += 2) {
      const hi = rle[i] & 0x0f;
      const lo = i + 1 < rle.length ? rle[i + 1] & 0x0f : 0;
      packed[i >> 1] = (hi << 4) | lo;
    }
    let bin = '';
    for (let i = 0; i < packed.length; i++) {
      bin += String.fromCharCode(packed[i]);
    }
    return 'R' + btoa(bin);
  }

  static fromBase64(b64: string): PixelArtData {
    if (b64.startsWith('R')) {
      return PixelArtData.fromRleBase64(b64.slice(1));
    }
    return PixelArtData.fromLegacyBase64(b64);
  }

  private static fromRleBase64(b64: string): PixelArtData {
    const bin = atob(b64);
    const packed = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      packed[i] = bin.charCodeAt(i);
    }
    // Unpack nibbles
    const nibbles: number[] = [];
    for (let i = 0; i < packed.length; i++) {
      nibbles.push((packed[i] >> 4) & 0x0f);
      nibbles.push(packed[i] & 0x0f);
    }
    return new PixelArtData(rleDecode(nibbles, PIXEL_GRID_SIZE * PIXEL_GRID_SIZE));
  }

  /** Decode legacy (v1) format without RLE. */
  private static fromLegacyBase64(b64: string): PixelArtData {
    const bin = atob(b64);
    const packed = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      packed[i] = bin.charCodeAt(i);
    }
    const total = PIXEL_GRID_SIZE * PIXEL_GRID_SIZE;
    const pixels = new Uint8Array(total);
    for (let i = 0; i < total; i += 2) {
      const byte = packed[i >> 1];
      pixels[i] = (byte >> 4) & 0x0f;
      if (i + 1 < total) pixels[i + 1] = byte & 0x0f;
    }
    return new PixelArtData(pixels);
  }

  /** Render to a canvas element and return a data URL (PNG). */
  toDataUrlSync(pixelScale = 1): string {
    const size = PIXEL_GRID_SIZE;
    const canvasSize = size * pixelScale;
    const canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    const ctx = canvas.getContext('2d')!;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = this.pixels[y * size + x];
        if (idx === 0) continue;
        ctx.fillStyle = PIXEL_PALETTE[idx];
        ctx.fillRect(x * pixelScale, y * pixelScale, pixelScale, pixelScale);
      }
    }
    return canvas.toDataURL('image/png');
  }
}

/**
 * RLE-encode an array of 4-bit values into nibble pairs: (count-1, value).
 * Max run per entry = 16 (nibble 0–15 represents counts 1–16).
 */
function rleEncode(data: Uint8Array): number[] {
  const out: number[] = [];
  let i = 0;
  while (i < data.length) {
    const val = data[i];
    let run = 1;
    while (i + run < data.length && data[i + run] === val && run < 16) {
      run++;
    }
    out.push(run - 1, val);
    i += run;
  }
  return out;
}

/** Decode RLE nibble pairs back into pixel values. */
function rleDecode(nibbles: number[], totalPixels: number): Uint8Array {
  const pixels = new Uint8Array(totalPixels);
  let pi = 0;
  for (let i = 0; i + 1 < nibbles.length && pi < totalPixels; i += 2) {
    const count = nibbles[i] + 1;
    const val = nibbles[i + 1];
    for (let j = 0; j < count && pi < totalPixels; j++) {
      pixels[pi++] = val;
    }
  }
  return pixels;
}
